import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { ConsumptionTotalsCreateDTO, ConsumptionTotalsReadDTO } from '../generated/api-client';
import { apiClient } from '../services/apiClient';

const buildTotalsQueryKey = (diagnosisId?: string, consumptionIdsKey: string = '') => [
  'consumption-totals',
  diagnosisId,
  consumptionIdsKey,
];

export const invalidateConsumptionTotalsQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  diagnosisId?: string
) => {
  if (diagnosisId) {
    queryClient.invalidateQueries(['consumption-totals', diagnosisId]);
    queryClient.invalidateQueries(`all-consumptions-for-balance-${diagnosisId}`);
    queryClient.invalidateQueries(['diagnosis-energy-balance', diagnosisId]);
  }
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (typeof key === 'string') {
        return key === 'consumption-totals';
      }
      if (Array.isArray(key) && key.length > 0) {
        return key[0] === 'consumption-totals';
      }
      return false;
    },
  });
};

export const useConsumptionTotalsByConsumptionIds = (
  diagnosisId?: string,
  consumptionIds: string[] = []
) => {
  const sortedIds = useMemo(() => [...consumptionIds].sort(), [consumptionIds]);
  const consumptionIdsKey = sortedIds.join('|');

  return useQuery<ConsumptionTotalsReadDTO[]>(
    buildTotalsQueryKey(diagnosisId, consumptionIdsKey),
    async () => {
      const results = await Promise.all(
        sortedIds.map(id =>
          apiClient.get_by_consumption_consumption_totals_by_consumption__consumption_id__get(id)
        )
      );
      return results.flat();
    },
    {
      enabled: Boolean(diagnosisId) && sortedIds.length > 0,
    }
  );
};

export const useCreateConsumptionTotal = (diagnosisId?: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: ConsumptionTotalsCreateDTO) =>
      apiClient.create_item_consumption_totals__post(payload),
    {
      onSuccess: () => {
        invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
      },
    }
  );
};

export const useUpdateConsumptionTotal = (diagnosisId?: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: ConsumptionTotalsCreateDTO }) =>
      apiClient.update_item_consumption_totals__item_id__put(id, data),
    {
      onSuccess: () => {
        invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
      },
    }
  );
};
